import { Dispatch, Fragment, MutableRefObject, PureComponent, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { MedicalHistoryConfig, MedicalHistorySection } from '../../../../../api/pushGenie';
import { Actions } from '../../../../../store/actions';
import {
    MEDICAL_HISTORY_CATEGORY,
    MHC,
    MHItem,
    MHItem2,
} from '../../../../../store/types/medicalHistory';
import MedicalHistoryCommonItemsIcon from '../../../../common/MedicalHistoryCommonItemsIcon';
import { medicalHistoryOptions } from '../../../../medicalHistoryConfigPanel/CustomiseSections';
import { MedicalHealthConfigAutoSuggest } from '../../../../medicalHistoryConfigPanel/MedicalHistoryConfigSearchInput';
import MedicalHistoryInputContainer from '../../../../medicalHistoryConfigPanel/MedicalHistoryInputContainer';
import PadV2MedicalHistoryCommonItemsTablePadV2 from './MedicalHistoryCommonItemsTablePadV2';
import { AnimatePresence } from 'framer-motion';
import PPMedicalHistoryConfigPanel from './MedicalHistoryConfigPanel';
import CheckableTag from 'antd/lib/tag/CheckableTag';
import RightPopUp from '../../../../common/RightPopUp';
import CommonItemsMH from './CommonItemsMH';

interface ConnectProps {
    medicalHistoryConfigData: MedicalHistoryConfig;
    isMedicalHistorySection?: boolean;
    onItemSave: (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2, index?: number) => void;
    onItemDelete: (catId: MEDICAL_HISTORY_CATEGORY, itemId: string) => void;
    onItemSwap: (catId: MEDICAL_HISTORY_CATEGORY, from: number, to: number) => void;
}
interface Props extends ConnectProps {
    dispatch: Dispatch<Actions>;
    patientHistoryData: MHC | undefined;
    showMedicalHistoryDetails: boolean;
    onConfigure?: () => void;
    onRefresh: () => void;
}

const MEDICAL_HISTORY = 'MEDICAL_HISTORY';

type State = {
    historyConfigDialogVisibility: boolean;
    activeMHItemPopUp?: MedicalHistorySection;
};

class MedicalHistoryItemsPadV2 extends PureComponent<Props> {
    state: State = {
        historyConfigDialogVisibility: false,
        activeMHItemPopUp: undefined,
    };

    openHistoryConfigDialog = () => {
        this.setState({ historyConfigDialogVisibility: true });
    };

    closeHistoryConfigDialog = () => {
        this.setState({ historyConfigDialogVisibility: false });
        const { onRefresh } = this.props;
        onRefresh();
    };

    setMHItemPopUp = (activeMHItemPopUp?: MedicalHistorySection) => {
        this.setState({ activeMHItemPopUp: activeMHItemPopUp });
    };

    saveItemsOnMedicalHistory = (item: MHItem, status: 'Active' | 'Inactive' | '') => {
        const { onItemSave } = this.props;

        const { catId } = item;
        const cloneItem = { ...item };
        delete cloneItem?.catId;

        catId && onItemSave(catId, { ...cloneItem, status: status });
    };

    deleteItemsFromMedicalHistory = (medicalHistoryConfigCommonItemsData: MHItem) => {
        const { onItemDelete } = this.props;

        const { catId, id } = medicalHistoryConfigCommonItemsData;
        catId && onItemDelete(catId, id);
    };

    changeStatus = (item: MHItem) => {
        switch (item?.status?.toLowerCase()) {
            case 'active':
                this.saveItemsOnMedicalHistory(item, 'Inactive');
                break;
            case 'inactive':
                this.saveItemsOnMedicalHistory(item, 'Active');
                break;
            default:
                this.saveItemsOnMedicalHistory(item, 'Active');
        }
    };

    render(): React.ReactNode {
        const {
            medicalHistoryConfigData,
            patientHistoryData,
            isMedicalHistorySection,
            showMedicalHistoryDetails,
            onItemSave,
            onItemDelete,
            onItemSwap,
            onConfigure,
        } = this.props;

        const { historyConfigDialogVisibility, activeMHItemPopUp } = this.state;

        const isAllSectionEmpty = !Object.values(patientHistoryData || {}).filter(
            (pmh) =>
                pmh
                    .filter(
                        (mh) =>
                            !medicalHistoryConfigData.commonItems.find(
                                (commonItem) => commonItem.id === mh.id,
                            ),
                    )
                    .filter(Boolean)?.length,
        )?.length;

        return (
            <Fragment>
                <AnimatePresence>
                    {historyConfigDialogVisibility && (
                        <PPMedicalHistoryConfigPanel closeMe={this.closeHistoryConfigDialog} />
                    )}
                </AnimatePresence>
                <CommonItemsMH
                    onItemSave={onItemSave}
                    onItemDelete={onItemDelete}
                    patientHistoryData={patientHistoryData}
                    isMedicalHistorySection={isMedicalHistorySection}
                    medicalHistoryOptions={medicalHistoryOptions}
                    StatusMessage={StatusMessage}
                    medicalHistoryConfigData={medicalHistoryConfigData}
                    openHistoryConfigDialog={this.openHistoryConfigDialog}
                    className="border-b-1 border-darwin-neutral-100 pb-16 "
                />

                {!isAllSectionEmpty && (
                    <div className="space-y-16 py-16 border-b-1 border-darwin-neutral-100">
                        {medicalHistoryConfigData.sections.map((section) => {
                            const patientHistoryDataExcludingCommonItems = patientHistoryData?.[
                                section.key
                            ]?.filter((item) => {
                                return !medicalHistoryConfigData.commonItems.find(
                                    (commonItem) =>
                                        commonItem.id === item.id &&
                                        commonItem.catId === section.key,
                                );
                            });

                            const isHide = !(
                                patientHistoryDataExcludingCommonItems &&
                                patientHistoryDataExcludingCommonItems?.length
                            );

                            if (isHide) {
                                return null;
                            }
                            return (
                                <div className="flex items-center whitespace-nowrap">
                                    <div className="flex items-center text-12 px-8 py-4 rounded-4 text-darwin-neutral-1000 bg-darwin-accent-dark-blue-200 uppercase space-x-6 mr-6">
                                        <p> {section.label}</p>
                                    </div>

                                    <div className="flex hide-scrollbar items-center space-x-24 pl-24 overflow-auto whitespace-nowrap">
                                        {patientHistoryDataExcludingCommonItems?.map(
                                            (mhData, i) => {
                                                return (
                                                    <>
                                                        {section.key ===
                                                            MEDICAL_HISTORY_CATEGORY.OTHER_MEDICAL_HISTORY ||
                                                        section.key ===
                                                            MEDICAL_HISTORY_CATEGORY.RECENT_TRAVEL_HISTORY
                                                            ? i !== 0 && (
                                                                  <div className="min-w-1 h-28 bg-darwin-neutral-500 "></div>
                                                              )
                                                            : null}

                                                        <MedicalHistoryCommonItemsIcon
                                                            hideIcon={
                                                                section.key ===
                                                                    MEDICAL_HISTORY_CATEGORY.OTHER_MEDICAL_HISTORY ||
                                                                section.key ===
                                                                    MEDICAL_HISTORY_CATEGORY.RECENT_TRAVEL_HISTORY
                                                            }
                                                            medicalHistoryConfigCommonItemsData={
                                                                mhData
                                                            }
                                                            status={mhData?.status?.toLowerCase()}
                                                            onClickIcon={() => {
                                                                this.changeStatus({
                                                                    ...mhData,
                                                                    catId: section.key,
                                                                });
                                                            }}
                                                            onClickText={() => {
                                                                const selectedMHItem = medicalHistoryConfigData?.sections.find(
                                                                    (sec) =>
                                                                        sec.key === section.key,
                                                                );

                                                                selectedMHItem &&
                                                                    this.setMHItemPopUp(
                                                                        selectedMHItem,
                                                                    );
                                                            }}
                                                            statusMessage={
                                                                mhData?.status?.toLowerCase()
                                                                    ? StatusMessage[
                                                                          mhData?.status?.toLowerCase()
                                                                      ]
                                                                    : '-'
                                                            }
                                                            text={mhData.name}
                                                        />
                                                    </>
                                                );
                                            },
                                        )}
                                        <div className="bg-white sticky right-0">
                                            <CheckableTag
                                                key={'add'}
                                                className="add-capsule"
                                                checked={false}
                                                onChange={() => {
                                                    const selectedMHItem = medicalHistoryConfigData?.sections.find(
                                                        (sec) => sec.key === section.key,
                                                    );

                                                    selectedMHItem &&
                                                        this.setMHItemPopUp(selectedMHItem);
                                                }}
                                            >
                                                {
                                                    <span className="space-x-4 text-14 text-darwin-primary-500">
                                                        <span>+</span>
                                                        <span className="mb-2">Add</span>
                                                    </span>
                                                }
                                            </CheckableTag>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="flex pt-16 m-0 overflow-hidden hover:overflow-scroll">
                    {medicalHistoryConfigData?.sections
                        ?.map((section, i) => {
                            const patientHistoryDataExcludingCommonItems = patientHistoryData?.[
                                section.key
                            ]?.filter((item) => {
                                return !medicalHistoryConfigData.commonItems.find(
                                    (commonItem) =>
                                        commonItem.id === item.id &&
                                        commonItem.catId === section.key,
                                );
                            });

                            const isHide =
                                patientHistoryDataExcludingCommonItems &&
                                patientHistoryDataExcludingCommonItems?.length;

                            if (isHide) {
                                return null;
                            }

                            return (
                                <CheckableTag
                                    className="add-capsule"
                                    onChange={() => {
                                        section && this.setMHItemPopUp(section);
                                    }}
                                    checked={false}
                                    key={section.key}
                                >
                                    <span className="space-x-4 text-14 text-darwin-primary-500">
                                        <span>+</span>
                                        <span className="mb-2">{section.label}</span>
                                    </span>
                                </CheckableTag>
                            );
                        })
                        .filter(Boolean)}
                </div>
                {activeMHItemPopUp && (
                    <CustomSectionRender
                        key={activeMHItemPopUp.key}
                        close={() => {
                            this.setMHItemPopUp(undefined);
                        }}
                        medicalHistoryConfigCommonItemsData={activeMHItemPopUp}
                        onItemSave={onItemSave}
                        onItemDelete={onItemDelete}
                        onItemSwap={onItemSwap}
                        patientHistoryData={patientHistoryData}
                        isMedicalHistorySection={isMedicalHistorySection}
                        showMedicalHistoryDetails={showMedicalHistoryDetails}
                        medicalHistoryConfigData={medicalHistoryConfigData}
                        isLastItem={false}
                    />
                )}
            </Fragment>
        );
    }
}

export default connect()(MedicalHistoryItemsPadV2);

const StatusMessage: {
    [key: string]: string;
} = {
    active: 'Y',
    inactive: 'N',
};

const CustomSectionInputContainer: React.FC<{
    medicalHistoryConfigCommonItemsData: MedicalHistorySection;
    onItemSave: (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2, index?: number) => void;
    isMedicalHistorySection?: boolean;
    patientHistoryData: MHC | undefined;
    medicalHistoryConfigData: MedicalHistoryConfig;
}> = ({
    medicalHistoryConfigCommonItemsData,
    onItemSave,
    isMedicalHistorySection,
    patientHistoryData,
    medicalHistoryConfigData,
}) => {
    if (
        medicalHistoryConfigCommonItemsData.key ===
            MEDICAL_HISTORY_CATEGORY.RECENT_TRAVEL_HISTORY ||
        medicalHistoryConfigCommonItemsData.key === MEDICAL_HISTORY_CATEGORY.OTHER_MEDICAL_HISTORY
    ) {
        return (
            <MedicalHistoryInputContainer
                setMedicalHistoryCommonItemsSectionData={onItemSave}
                selectedSection={medicalHistoryConfigCommonItemsData.key}
                isMedicalHistorySection={isMedicalHistorySection}
            />
        );
    }

    return (
        <div className="bg-white rounded-8">
            <MedicalHealthConfigAutoSuggest
                isMedicalHistorySection={isMedicalHistorySection}
                setMedicalHistoryCommonItemsSectionData={onItemSave}
                medicalHistoryCommonItems={[
                    ...(patientHistoryData?.[medicalHistoryConfigCommonItemsData.key] || []),
                    ...(medicalHistoryConfigData?.commonItems || []),
                ]}
                selectedSection={medicalHistoryConfigCommonItemsData.key}
                api={medicalHistoryOptions[medicalHistoryConfigCommonItemsData.key].func}
                elidPrefixId={MEDICAL_HISTORY}
            />
        </div>
    );
};

export const MH_POP_UP = 'MH_POP_UP';

const CustomSectionRender: React.FC<{
    medicalHistoryConfigCommonItemsData: MedicalHistorySection;
    showMedicalHistoryDetails: boolean;
    patientHistoryData: MHC | undefined;
    isMedicalHistorySection?: boolean;
    onItemSave: (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2, index?: number) => void;
    onItemDelete: (catId: MEDICAL_HISTORY_CATEGORY, itemId: string) => void;
    onItemSwap: (catId: MEDICAL_HISTORY_CATEGORY, from: number, to: number) => void;
    isLastItem: boolean;
    close: () => void;
    medicalHistoryConfigData: MedicalHistoryConfig;
}> = ({
    medicalHistoryConfigCommonItemsData,
    patientHistoryData,
    isMedicalHistorySection,
    showMedicalHistoryDetails,
    onItemSave,
    onItemDelete,
    onItemSwap,
    isLastItem,
    close,
    medicalHistoryConfigData,
}) => {
    const [tableRefActive, setTableRefActive] = useState(false);
    const elements: MutableRefObject<MHItem2[]> = useRef([]);
    elements.current =
        (patientHistoryData?.[medicalHistoryConfigCommonItemsData.key] as MHItem2[]) || [];

    const onSave = (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2, index?: number) => {
        onItemSave(catId, item, index);
        setTableRefActive(true);
    };

    return (
        <RightPopUp width="70%" closeMe={close}>
            <div className="flex flex-col bg-darwin-neutral-50 h-full">
                <div className="flex items-center justify-between p-16 border border-darwin-neutral-200">
                    <div className="flex items-center space-x-8">
                        <div className="rounded-4 min-w-32 h-32 flex items-center justify-center bg-darwin-accent-medical-history-orange-900">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                role="img"
                                viewBox="0 0 11 13"
                                className="w-11 h-12 fill-white w-14 h-16"
                            >
                                <path d="M10.75 8.375V1.625C10.75 1.01562 10.2344 0.5 9.625 0.5H2.5C1.23438 0.5 0.25 1.50781 0.25 2.75V10.25C0.25 11.5156 1.23438 12.5 2.5 12.5H10C10.3984 12.5 10.75 12.1719 10.75 11.7734C10.75 11.5156 10.5859 11.2812 10.375 11.1406V9.24219C10.5859 9.00781 10.75 8.72656 10.75 8.375ZM3.25 4.39062C3.25 4.20312 3.41406 4.01562 3.625 4.01562H4.86719V2.75C4.86719 2.5625 5.03125 2.375 5.24219 2.375H6.46094C6.64844 2.375 6.83594 2.5625 6.83594 2.75V4.01562H8.125C8.3125 4.01562 8.5 4.20312 8.5 4.39062V5.60938C8.5 5.82031 8.3125 5.98438 8.125 5.98438H6.85938V7.25C6.85938 7.46094 6.67188 7.625 6.48438 7.625H5.26562C5.05469 7.625 4.89062 7.46094 4.89062 7.25V5.98438H3.625C3.41406 5.98438 3.25 5.82031 3.25 5.60938V4.39062ZM9.25 11H2.5C2.07812 11 1.75 10.6719 1.75 10.25C1.75 9.85156 2.07812 9.5 2.5 9.5H9.25V11Z"></path>
                            </svg>
                        </div>
                        <p className="text-20 font-700">
                            {medicalHistoryConfigCommonItemsData.label}
                        </p>
                    </div>
                    <div className="w-24 h-24 cursor-pointer" onClick={close}>
                        <svg
                            className="w-24 h-24 fill-neutral-1"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 320 512"
                        >
                            <path d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z"></path>
                        </svg>
                    </div>
                </div>

                <div
                    className={`flex flex-1  flex-col  isLastItem  ${
                        isLastItem ? 'pt-16' : 'py-16'
                    } `}
                    id={MH_POP_UP}
                >
                    <div className="px-16 space-y-24">
                        <CustomSectionInputContainer
                            medicalHistoryConfigCommonItemsData={
                                medicalHistoryConfigCommonItemsData
                            }
                            patientHistoryData={patientHistoryData}
                            isMedicalHistorySection={isMedicalHistorySection}
                            onItemSave={onSave}
                            medicalHistoryConfigData={medicalHistoryConfigData}
                        />

                        <PadV2MedicalHistoryCommonItemsTablePadV2
                            isMedicalHistorySection={isMedicalHistorySection}
                            catId={medicalHistoryConfigCommonItemsData.key}
                            patientHistoryData={patientHistoryData}
                            setMedicalHistoryCommonItemsSectionData={onItemSave}
                            deleteMedicalHistoryCommonItemsSectionData={onItemDelete}
                            swapMedicalHistoryCommonItemsSectionData={onItemSwap}
                            tableRefActive={tableRefActive}
                            setTableRefActive={setTableRefActive}
                        />
                    </div>
                </div>
            </div>
        </RightPopUp>
    );
};
